/**
 * SystemTemplateBrowserDialog — browse wrapper that drives two flows from
 * one shell:
 *
 *   1. **Import** mode (default) — browse nested
 *      `system_platforms/{platformId}/post_templates/*` catalogue with bulk-import semantics. Multi-select checkboxes,
 *      "Import selected" + "Import all (filtered)" actions, "Imported"
 *      badges. Row click → `PostTemplateEditDialog` in view mode.
 *
 *   2. **Pick** mode — single-select picker over an externally-supplied
 *      template list (e.g. per-author per-platform `post_templates/*`).
 *      Row click immediately fires `onPick(templateId)` + closes. No
 *      checkboxes, no bulk actions, no preview detour.
 *
 * Multi-select chip filters for goal and lengthBucket apply to both modes.
 * Tone is NOT exposed as filter (it's per-author per-platform via
 * PlatformVoice). Platform is supplied via `platformPrefilter` and
 * applied as an `'X' OR 'any'` client-side filter.
 *
 * Data source override: when `templates` is supplied, the system catalogue
 * hook is bypassed (used by Pick mode for per-author libraries).
 */

import React, { useMemo, useState } from 'react';
import { Box, Stack, Chip, Typography, Alert, Button } from '@mui/material';
import {
  SDialog,
  SDataTable,
  type SDataTableRowSelectionState,
} from '@modules/ui-components';
import { useSystemPostTemplates } from '../hooks/useSystemPostTemplates';
import type { PostTemplate } from '@segmently/schema-models/content-plan';
import { PostTemplateEditDialog } from './PostTemplateEditDialog';
import { buildPostTemplateColumns } from './postTemplateColumns';

const DEFAULT_GOALS = [
  'branding',
  'leadgen',
  'engagement',
  'education',
  'recruiting',
  'launch',
] as const;
const LENGTH_BUCKETS = ['short', 'medium', 'long'] as const;

export interface SystemTemplateBrowserDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-filter to a single platform; matches `platform === X || 'any'`. */
  platformPrefilter?: string;
  /** templateIds already imported into the caller's overlay. Used for "Imported" badge + skip in bulk import. */
  importedTemplateIds?: Set<string>;
  /**
   * Bulk import callback. Receives the array of system templateIds the
   * user selected (or all filtered when "Import all" pressed). Caller
   * dispatches per-template imports.
   */
  onImport?: (templateIds: string[]) => Promise<void> | void;
  /** Reserved for /admin/system-templates page. Not implemented this iteration. */
  adminMode?: boolean;
  /**
   * Picker mode — when 'pick', the dialog runs as a multi-select picker:
   * checkboxes pre-checked from `selectedIds`, "Save N selected" primary
   * action calls `onPick(ids)` + closes, row click opens the same view-
   * mode preview that import mode uses. Default 'import' = bulk-import
   * over the system catalogue (existing behaviour).
   */
  mode?: 'import' | 'pick';
  /** Multi-select callback — required when mode='pick'. Receives the full new selection. */
  onPick?: (templateIds: string[]) => void;
  /**
   * Optional external template source. When provided, replaces the
   * default `useSystemPostTemplates` catalogue read. Used by Pick mode
   * to feed in a per-author per-platform library.
   */
  templates?: PostTemplate[];
  /** Loading flag when `templates` is provided externally. */
  loading?: boolean;
  /** Override dialog title. */
  title?: string;
  /** Override the explainer line beneath the title. */
  subtitle?: string;
  /**
   * Pre-selected ids in Pick mode. Drives initial checkbox state AND
   * renders a "Selected" badge on rows already saved before this open.
   */
  selectedIds?: string[];
}

export const SystemTemplateBrowserDialog: React.FC<SystemTemplateBrowserDialogProps> = ({
  open,
  onClose,
  platformPrefilter,
  importedTemplateIds,
  onImport,
  adminMode = false,
  mode = 'import',
  onPick,
  templates: externalTemplates,
  loading: externalLoading,
  title: titleOverride,
  subtitle: subtitleOverride,
  selectedIds: pickSelectedIds,
}) => {
  const isPick = mode === 'pick';

  // Initial multi-select state in pick mode is seeded from the caller's
  // `selectedIds` (templates already saved to the topic before this open).
  // Memoised by `pickSelectedIds` reference so re-opens reset to canonical.
  const initialRowSelection = useMemo<SDataTableRowSelectionState>(() => {
    if (!isPick || !pickSelectedIds || pickSelectedIds.length === 0) return {};
    return Object.fromEntries(pickSelectedIds.map((id) => [id, true]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPick, JSON.stringify(pickSelectedIds ?? [])]);

  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedBuckets, setSelectedBuckets] = useState<string[]>([]);
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>(initialRowSelection);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Reset checkbox state to canonical when the dialog re-opens with a
  // different `selectedIds` set — e.g. user opens picker for tiktok after
  // having opened it for linkedin in the same session.
  React.useEffect(() => {
    setRowSelection(initialRowSelection);
  }, [initialRowSelection]);

  // Externally-supplied template list (Pick mode) bypasses the catalogue read.
  const catalogueFilter = useMemo(
    () => (platformPrefilter ? { platform: platformPrefilter } : {}),
    [platformPrefilter],
  );
  const {
    items: catalogueItems,
    loading: catalogueLoading,
    error: catalogueError,
  } = useSystemPostTemplates(catalogueFilter);
  const items = externalTemplates ?? catalogueItems;
  const loading = externalTemplates !== undefined ? Boolean(externalLoading) : catalogueLoading;

  // Set of ids already saved to the topic before this dialog opened — used
  // for the "Selected" badge on rows in pick mode.
  const savedSelectedSet = useMemo(
    () => new Set(pickSelectedIds ?? []),
    [pickSelectedIds],
  );

  // Client-side filter: platform prefilter (always applied) + multi-select goal + multi-select length.
  const filteredItems = useMemo(() => {
    return items.filter((t) => {
      if (
        platformPrefilter &&
        t.platform !== platformPrefilter &&
        t.platform !== 'any'
      ) {
        return false;
      }
      if (selectedGoals.length > 0 && !selectedGoals.includes(t.goal ?? '')) return false;
      if (selectedBuckets.length > 0 && !selectedBuckets.includes(t.lengthBucket ?? '')) return false;
      return true;
    });
  }, [items, platformPrefilter, selectedGoals, selectedBuckets]);

  const importedCount = useMemo(
    () =>
      filteredItems.filter((t) => importedTemplateIds?.has(t.id)).length,
    [filteredItems, importedTemplateIds],
  );

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((k) => rowSelection[k]),
    [rowSelection],
  );

  const importableSelectedIds = useMemo(
    () => selectedIds.filter((id) => !importedTemplateIds?.has(id)),
    [selectedIds, importedTemplateIds],
  );

  const handlePickSave = () => {
    if (!isPick || !onPick) return;
    onPick(selectedIds);
    onClose();
  };

  const handleImportSelected = async () => {
    if (!onImport || importableSelectedIds.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      await onImport(importableSelectedIds);
      setRowSelection({});
    } catch (e: any) {
      setError(e?.message ?? 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImportAll = async () => {
    if (!onImport) return;
    const ids = filteredItems
      .map((t) => t.id)
      .filter((id) => !importedTemplateIds?.has(id));
    if (ids.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      await onImport(ids);
      setRowSelection({});
    } catch (e: any) {
      setError(e?.message ?? 'Bulk import failed');
    } finally {
      setImporting(false);
    }
  };

  const previewTemplate = useMemo(
    () => (previewId ? items.find((t) => t.id === previewId) ?? null : null),
    [previewId, items],
  );

  const columns = useMemo(
    () => buildPostTemplateColumns({
      savedSelectedSet: isPick ? savedSelectedSet : undefined,
      importedTemplateIds: !isPick ? importedTemplateIds : undefined,
    }),
    [importedTemplateIds, isPick, savedSelectedSet],
  );

  return (
    <>
      {/*
       * SDialog convention (per SDialog.stories.tsx → "Default" variant):
       *   X in top-LEFT handles dismiss; primaryAction in footer.
       *   Never combine the default X with a Cancel-labelled secondary —
       *   that's the documented anti-pattern.
       */}
      <SDialog
        open={open}
        onClose={onClose}
        title={
          titleOverride ??
          (isPick
            ? 'Pick template'
            : adminMode
              ? 'Admin — System Post Templates'
              : 'Browse System Catalogue')
        }
        maxWidth="lg"
        fullWidth
        data-testid="system-template-browser"
        // Fix dialog size — independent of how many rows the current
        // filter produces. Avoids the "dialog jumps" UX when toggling
        // chips. 85vh leaves comfortable margins on standard laptops.
        PaperProps={{
          sx: {
            height: '85vh',
            maxHeight: '85vh',
          },
        }}
        primaryAction={
          isPick
            ? {
                label:
                  selectedIds.length > 0
                    ? `Save ${selectedIds.length} selected`
                    : 'Save',
                onClick: handlePickSave,
                disabled: !onPick,
                'data-testid': 'system-template-pick-save-btn',
              }
            : onImport
              ? {
                  label:
                    importableSelectedIds.length > 0
                      ? `Import ${importableSelectedIds.length} selected`
                      : 'Import',
                  onClick: handleImportSelected,
                  loading: importing,
                  disabled: importableSelectedIds.length === 0 || importing,
                  'data-testid': 'system-template-import-selected-btn',
                }
              : undefined
        }
      >
        <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {subtitleOverride ??
              (isPick
                ? 'Tick the templates you want this topic to use, click a row to preview, then Save. Filter by goal or length.'
                : onImport
                  ? `Pick templates to import into this author / platform. Imports start as full copies you can edit per-author. ${importedCount}/${filteredItems.length} already imported.`
                  : 'Manage the system catalogue. Edits propagate to projects via the next seed/refresh-from-catalogue cycle.')}
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {catalogueError && (
            <Alert severity="error">
              {catalogueError.message}
            </Alert>
          )}

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <FilterChips
              label="Goal"
              options={DEFAULT_GOALS as unknown as string[]}
              selected={selectedGoals}
              onChange={setSelectedGoals}
              testid="filter-goal"
            />
            <FilterChips
              label="Length"
              options={LENGTH_BUCKETS as unknown as string[]}
              selected={selectedBuckets}
              onChange={setSelectedBuckets}
              testid="filter-length"
            />
            <Box flexGrow={1} />
            {!isPick && onImport && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleImportAll}
                disabled={importing || filteredItems.every((t) => importedTemplateIds?.has(t.id))}
                data-testid="system-template-import-all-btn"
              >
                Import all (
                {filteredItems.filter((t) => !importedTemplateIds?.has(t.id)).length})
              </Button>
            )}
          </Stack>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading templates…
              </Typography>
            ) : filteredItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No templates match the current filters.
              </Typography>
            ) : (
              <SDataTable<PostTemplate>
                columns={columns}
                data={filteredItems}
                getRowId={(row) => row.id}
                enableRowSelection={isPick || Boolean(onImport)}
                enableSorting
                enableTopToolbar={false}
                enableBottomToolbar={filteredItems.length > 25}
                enablePagination={filteredItems.length > 25}
                state={{ rowSelection }}
                onRowSelectionChange={setRowSelection}
                initialState={{ pagination: { pageIndex: 0, pageSize: 25 } }}
                muiTablePaperProps={{ sx: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } }}
                muiTableContainerProps={{ sx: { flex: 1, minHeight: 0, overflow: 'auto' } }}
                muiSelectCheckboxProps={{ size: 'small' }}
                muiSelectAllCheckboxProps={{ size: 'small' }}
                muiTableBodyRowProps={({ row }) => ({
                  hover: true,
                  // Row click ALWAYS opens preview — both 'import' and 'pick'
                  // modes use the same affordance. Selection toggles via the
                  // checkbox column, not the row click.
                  onClick: () => setPreviewId(row.original.id),
                  sx: { cursor: 'pointer' },
                })}
              />
            )}
          </Box>
        </Stack>
      </SDialog>

      {previewTemplate && (
        <PostTemplateEditDialog
          open={Boolean(previewTemplate)}
          onClose={() => setPreviewId(null)}
          template={previewTemplate}
          mode="view"
          title={`System template — ${previewTemplate.name || previewTemplate.id}`}
        />
      )}
    </>
  );
};

const FilterChips: React.FC<{
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  testid?: string;
}> = ({ label, options, selected, onChange, testid }) => {
  const toggle = (opt: string) => {
    onChange(
      selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt],
    );
  };
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, fontWeight: 500 }}>
        {label}
      </Typography>
      {options.map((opt) => {
        const on = selected.includes(opt);
        return (
          <Chip
            key={opt}
            label={opt}
            size="small"
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            onClick={() => toggle(opt)}
            data-testid={testid ? `${testid}-${opt}` : undefined}
            sx={{ cursor: 'pointer' }}
          />
        );
      })}
    </Stack>
  );
};

export default SystemTemplateBrowserDialog;
